/**
 * imageForAI (Jose 2026-09-25) — turn ANY camera/library image into something the vision model accepts.
 *
 * WHY: iPhone photos are HEIC by default. Anthropic vision only accepts JPEG/PNG/GIF/WebP, so a raw HEIC
 * upload fails with "Could not process image" (proven live: same call with a JPEG succeeds). Blocking HEIC
 * would kill the feature — every iPhone photo is HEIC. Instead we DECODE it: Safari / WKWebView (Capacitor)
 * decode HEIC natively into an <img>, so we draw it to a canvas, downscale (keeps us under the ~5MB /
 * oversized-dimension limit), and re-encode as JPEG. One helper, used by every AI photo consumer
 * (Visual Count, Use Product, Invoice-photo import). PDFs do NOT come through here — they go raw to
 * extractDocument. See functions/src/aiService.ts (MAX_IMAGE_B64) and the shared aiService brain.
 */

const MAX_DIM = 1600; // longest side in px — plenty for label/shelf OCR, keeps base64 well under the limit

export interface AiImage {
  base64: string; // no data: prefix
  mediaType: string;
}

/** Raw base64 + media type — the honest fallback when a browser genuinely can't decode the file. */
function rawBase64(file: File): Promise<AiImage> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ base64: String(r.result || '').split(',')[1] || '', mediaType: file.type || 'image/jpeg' });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Decode → downscale → JPEG. The returned mediaType is always what actually left the canvas
 * (image/jpeg), so bytes and media_type can never mismatch. Falls back to raw bytes only for the rare
 * input the browser can't decode at all.
 */
export async function fileToJpegBase64(file: File, maxDim = MAX_DIM): Promise<AiImage> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('decode failed'));
      im.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error('empty image');
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(img, 0, 0, cw, ch);
    const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1] || '';
    if (!base64) throw new Error('encode failed');
    return { base64, mediaType: 'image/jpeg' };
  } catch {
    return rawBase64(file);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Decode (incl. iPhone HEIC) → downscale → JPEG FILE, ready to upload to Storage. Same pipeline as
 * fileToJpegBase64 but returns a File so uploadBytes receives web-safe, optimized bytes. Falls back to the
 * original file only if the browser genuinely can't decode it. (Jose 2026-10-17 — fixes service/product
 * photos that wouldn't render because a raw HEIC/odd-format file got stored verbatim.)
 */
export async function fileToWebImageFile(file: File, maxDim = 1200, quality = 0.85): Promise<File> {
  if (!file || !file.type?.startsWith?.('image/') && !/\.(heic|heif|png|jpe?g|webp)$/i.test(file.name || '')) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('decode failed'));
      im.src = url;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error('empty image');
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(img, 0, 0, cw, ch);
    const blob: Blob = await new Promise((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode failed'))), 'image/jpeg', quality));
    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file; // browser couldn't decode (rare) — upload the original rather than lose it
  } finally {
    URL.revokeObjectURL(url);
  }
}
